/* eslint-disable react/prop-types */
import {
  ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { shallowEqual } from 'react-redux';
import {
  CONFIRM_FAILED_REQUEST_AI_CHARACTER,
  CREATE_AI_CHARACTER,
  GET_PRESIGNED_URLS,
  GET_PROMPTS,
  GET_SELECTED_REQUEST_AI_CHARACTER,
  GET_SESSION_ID,
  LIST_GENERATED_IMAGE_REQUEST,
  LIST_MAIN_CHARACTER,
  UPSERT_STORYGAME_CHARACTER,
} from '@api/story';
import {
  cropImageTopCenterFromUrl,
  isMobileDevice,
  margeChoiceProps,
} from '@common/utils';
import { useAppSelector } from '@store/hooks';
import { RootState } from '@store/store';
import {
  AiProfileInfo,
  CustomProfile,
  GetPresignedUrlsOutput,
  MyCharacterInfo,
  OnboardingAiProfileType,
  OnboardingPopup,
  OtherCharacterInfo,
  PlayStory,
} from '@customTypes/chapter';
import {
  AiCharacterImageType,
  ImageFile,
  ListGeneratedImageRequest,
  ListGeneratedImageResponse,
  ListMainCharacterData,
  MainCharacter,
  PromptFilterOption,
  RequestAICharacter,
  RequestCreateAICharacter,
  RequestGetPrompts,
  ResponseGetPromptsData,
} from '@/customTypes/storygame';
import { uniqBy } from 'lodash';
import useLocalStorage from '@/hooks/useLocalStorage';
import { SERVER_ERROR_CODE, STORAGE_KEY } from '@/common/values';
import { IStoryGameOnboardingAlert } from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import { useRouter } from 'next/router';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { goBackForApp, onLogEventForApp } from '@/lib/webview';
import { createCustomCharacterComplete } from '@/lib/eventManager';
import useTranslation from 'next-translate/useTranslation';

interface IOnboardingPopup {
  playStory?: PlayStory;
  onboardings: OnboardingPopup[];
  onboarding?: OnboardingPopup;
  profileType?: 'UpdateProfile' | 'ImageUpload' | 'ChooseProfile';
  isLoading: boolean;
  onboardingIndex: React.MutableRefObject<number>;
  choiceProfilesRef: React.MutableRefObject<CustomProfile[]>;
  currentNameRef: React.MutableRefObject<
    | {
        firstName: string;
        lastName?: string | undefined;
      }
    | undefined
  >;
  aiProfileType: OnboardingAiProfileType;
  aiProfileInfoArray?: AiProfileInfo[];
  aiProfileInfo?: AiProfileInfo; // 현재 선택된 캐릭터의 aiProfileInfo
  aiCharacterSessionId?: string;
  aiCharacterPromptOptions?: PromptFilterOption;
  aiCharacterImageList?: AiCharacterImageType[];
  characterList?: MainCharacter[];
  selectedCharacter?: MainCharacter;
  openedStorygameIds?: string;
  isShowPromotionStorygame: boolean;
  storygamePopupBg?: string;
  isStoryGameEdit: boolean;
  storygameAlertData: IStoryGameOnboardingAlert;
  storygameToastData: { message: string; isVisible: boolean };
  onShowProfile: (type: 'default' | 'user') => void;
  onCloseSelectImage: (customProfile?: { file: File; fileUrl: string }) => void;
  onSelectImage: () => void;
  onConfirmChooseProfile: (image: CustomProfile) => void;
  onNextPopup: () => void;
  onUpdateProfileImage: (currentName?: {
    firstName: string;
    lastName?: string;
  }) => void;
  setName: (
    otherCharacterInfo?: OtherCharacterInfo & {
      popupId: number;
    },
    myCharacterInfo?: MyCharacterInfo & {
      popupId: number;
    },
  ) => void;
  onNextChoice: (
    mProps?: Record<string, number | string>,
    profileInfo?: CustomProfile,
  ) => void;
  onCloseUpdateProfile: () => void;
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onCreateAiCharacter: (data: RequestCreateAICharacter) => void;
  onGetAiCharacterSessionId: () => Promise<void>;
  onGetAiCharacterPromptOptions: () => Promise<void>;
  onGetAiCharacterImageList: (chrId?: number) => Promise<void>;
  onGetMainCharacterList: () => Promise<MainCharacter[] | undefined>;
  onClickStoryGameCharacter: (data: AiProfileInfo) => void;

  onCreateCharacter: () => Promise<void>;
  addOpenedStorygameIds: () => void;
  setIsShowPromotionStorygame: (value: boolean) => void;
  setIsStoryGameEdit: (value: boolean) => void;
  selectPreviousCharacter: () => void;
  selectNextCharacter: () => void;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  isNameMissingForSave: () => boolean;
  onClickHomeIcon: () => void;
  onClickMyImage: (character: AiCharacterImageType) => void;
  setStorygameToastData: (data: {
    message: string;
    isVisible: boolean;
  }) => void;
  removeSessionId: () => void;
}

const OnboardingPopupContext = createContext<IOnboardingPopup | undefined>(
  undefined,
);

const OnboardingPopupProvider = ({
  children,
  onFinish,
}: {
  children: JSX.Element | Array<JSX.Element>;
  onFinish: (
    myCharacterInfo?: MyCharacterInfo,
    choiceProps?: Record<string, number | string>,
    otherCharacterInfoList?: OtherCharacterInfo[],
    playerClassId?: number,
  ) => void;
}) => {
  const { t } = useTranslation();
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const router = useRouter();
  const [playStory, setPlayStory] = useState<PlayStory>();
  const [onboardings, setOnboardings] = useState<OnboardingPopup[]>([]);
  const [profileType, setProfileType] = useState<
    'UpdateProfile' | 'ImageUpload' | 'ChooseProfile' | undefined
  >();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onboardingIndex = useRef<number>(0);
  const [onboarding, setOnboarding] = useState<OnboardingPopup>();
  const choicePropsRef = useRef<Record<string, number | string>>();
  const otherCharacterInfosRef = useRef<
    (OtherCharacterInfo & { popupId?: number })[]
  >([]);
  const myCharacterInfoRef = useRef<MyCharacterInfo & { popupId?: number }>();
  const choiceProfilesRef = useRef<CustomProfile[]>([]);
  const isChooseProfileRef = useRef<boolean>(false);
  const presignedUrlsRef = useRef<
    (GetPresignedUrlsOutput & { file?: File; popupId?: number })[]
  >([]);
  const currentNameRef = useRef<{
    firstName: string;
    lastName?: string;
  }>();

  // 스겜전용 변수 시작, StoryGameAiCharacterImagePopup 즉 AiCharacterImage type일 경우만 사용한다. ------
  const [aiProfileType, setAiProfileType] = useState<OnboardingAiProfileType>(); // AiCharacterImage, AiCharacterList type일 경우만 사용한다. undefined, upload, AiImageSelect 3가지 타입이 있다.
  //  스토리게임에서 AI 프로필 만들 때 서버에 넘겨줘야 하는 데이터. firstName, lastName, aiProfileImageId, profileFile, characterId(-1 = 주인공).
  const [aiProfileInfoArray, setAiProfileInfoArray] = useState<AiProfileInfo[]>(
    [],
  );
  const [aiProfileInfo, setAiProfileInfo] = useState<AiProfileInfo>(); // 현재 선택된 캐릭터의 aiProfileInfo
  const [characterList, setCharacterList] = useState<MainCharacter[]>(); // 캐릭터 목록(좌측)
  const [aiCharacterImageList, setAiCharacterImageList] =
    useState<AiCharacterImageType[]>(); // 캐릭터 목록(우측)
  // 현재 선택된 캐릭터.
  const [selectedCharacter, setSelectedCharacter] = useState<MainCharacter>();
  const [aiCharacterSessionId, setAiCharacterSessionId] = useState<
    string | undefined
  >();
  const [aiCharacterPromptOptions, setAiCharacterPromptOptions] =
    useState<PromptFilterOption>();
  const [openedStorygameIds, setOpenedStorygameIds] = useLocalStorage(
    STORAGE_KEY.OPENED_STORYGAME_IDS,
  );
  const [isShowPromotionStorygame, setIsShowPromotionStorygame] =
    useState<boolean>(false);
  const [isStoryGameEdit, setIsStoryGameEdit] = useState<boolean>(true);
  const [storygameAlertData, setStorygameAlertData] =
    useState<IStoryGameOnboardingAlert>({
      title: '',
      subtitle: '',
      onCancel: () => {
        // do nothing
      },
      onConfirm: () => {
        // do nothing
      },
      isVisible: false,
    });

  const [storygameToastData, setStorygameToastData] = useState<{
    message: string;
    isVisible: boolean;
  }>({
    message: '',
    isVisible: false,
  });

  const { isPC } = useWindowWidth();
  const sessionArray = JSON.parse(
    localStorage.getItem(STORAGE_KEY.STORY_GAME_SESSION_ID) ?? '[]',
  ) as { storyId: number; sessionId: string }[];

  const { data: sessionData } = useQuery<{
    requestSession: { sessionId: string };
  }>(GET_SESSION_ID, {
    skip: sessionArray.some(
      (item) => item.storyId === playChapterInfo?.playChapter.storyId,
    ), // storyId가 있으면 스킵. 객체가 있다면 무조건 sessionId는 필수값
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const matchingSession = sessionArray.find(
      (item) => item.storyId === playChapterInfo?.playChapter.storyId,
    );

    if (matchingSession) {
      setAiCharacterSessionId(matchingSession.sessionId);
    } else {
      setAiCharacterSessionId(undefined);
    }
  }, [playChapterInfo?.playChapter.storyId, sessionArray]);

  const [getMainCharacterList] = useLazyQuery<ListMainCharacterData>(
    LIST_MAIN_CHARACTER,
    { fetchPolicy: 'no-cache' },
  );

  const [getAiCharacterPromptOptions] = useLazyQuery<
    ResponseGetPromptsData,
    RequestGetPrompts
  >(GET_PROMPTS);

  // AI 캐릭터 이미지 목록을 가져오는 쿼리
  const [getAiCharacterImageList] = useLazyQuery<
    ListGeneratedImageResponse,
    ListGeneratedImageRequest
  >(LIST_GENERATED_IMAGE_REQUEST, {
    fetchPolicy: 'no-cache', // 캐시를 사용하지 않고 항상 새로운 데이터를 가져옵니다
    pollInterval: 5000, // 5초마다 자동으로 폴링합니다
    notifyOnNetworkStatusChange: true, // 네트워크 상태 변경을 감지합니다
    onCompleted: async (res) => {
      // res.listRequestAICharacter.list 에서 status === Failed 이면 오류 팝업 표시 후 list에서 제거

      const failedImage = res.listRequestAICharacter.list.find(
        (item) => item.status === 'Failed',
      );
      // 리스트에서 제거

      if (playChapterInfo?.playChapter) {
        const defaultImageFile =
          res.listRequestAICharacter.chrId === -1
            ? playChapterInfo?.playChapter.story?.mainCharacterPortraitFile
            : playChapterInfo?.playChapter.story.characters.find(
                (c) => c.chrId === res.listRequestAICharacter.chrId,
              )?.portraitFile;
        const defaultProfile =
          res.listRequestAICharacter.chrId === -1
            ? playChapterInfo?.playChapter.story?.mainCharacterImageFile
            : playChapterInfo?.playChapter.story.characters.find(
                (c) => c.chrId === res.listRequestAICharacter.chrId,
              )?.imageFile;

        const aiSavedImage = await getSavedAiCharacterImage({
          // 저장된건 서버에서같은 api로 내려줄 수 없어서 Api 분리
          variables: {
            chrId: res.listRequestAICharacter.chrId ?? -1,
            storyId: playChapterInfo?.playChapter.storyId,
          },
        });

        const concatList: AiCharacterImageType[] = [];

        if (aiSavedImage.data?.getSelectedRequestAICharacter) {
          concatList.push({
            id: aiSavedImage.data.getSelectedRequestAICharacter.id,
            imageFile:
              aiSavedImage.data.getSelectedRequestAICharacter.imageFile,
            isDefault: false,
            profileImage: characterList?.find(
              (d) =>
                d.id === aiSavedImage.data?.getSelectedRequestAICharacter.chrId,
            )?.imageFile as ImageFile,
          });
        }
        concatList.push({
          id: -1,
          imageFile: defaultImageFile as unknown as ImageFile,
          profileImage: defaultProfile as ImageFile,
          isDefault: true,
        });

        if (failedImage) {
          const datalist = res?.listRequestAICharacter.list
            .filter((item) => item.id !== failedImage.id)
            .map((item) => ({
              id: item.id,
              imageFile: item.imageFile,
              isDefault: false,
            }));
          setAiCharacterImageList(
            uniqBy(
              [...concatList.reverse(), ...datalist.reverse()],
              'id',
            ).reverse(),
          );
          // 서버에 실패한 이미지 삭제 요청
          await imageConfirmFailRequest({ variables: { id: failedImage.id } });

          // 실패한 이미지 사용 횟수 증가
          const defaultProfileData = aiProfileInfoArray.map((item) => {
            if (item.characterId === res.listRequestAICharacter.chrId) {
              return {
                ...item,
                usedAd: (item.usedAd ?? 0) + 1,
              };
            }
            return item;
          });

          setAiProfileInfoArray(defaultProfileData);

          setStorygameAlertData({
            title: t('storygame_ai_onboarding_popup_title_generation_error'),
            subtitle: t(
              'storygame_ai_onboarding_popup_description_generation_error',
            ),
            onConfirm: () => {
              setStorygameAlertData({
                ...storygameAlertData,
                isVisible: false,
              });

              const foundDefault = aiCharacterImageList?.find(
                (item) => item.isDefault,
              );
              const firstImage = aiCharacterImageList?.[0];

              // 디폴트 이미지로 변환하고, 디폴트 이미지가 없으면 첫번째 이미지로 변환한다.
              if (foundDefault) {
                onClickMyImage(foundDefault);
              } else if (firstImage) {
                onClickMyImage(firstImage);
              }

              setAiProfileType('AiImageFilter');
            },
            isVisible: true,
          });
        } else {
          const datalist = res?.listRequestAICharacter.list.map((item) => ({
            id: item.id,
            imageFile: item.imageFile,
            isDefault: false,
          }));
          setAiCharacterImageList(
            uniqBy(
              [...concatList.reverse(), ...datalist.reverse()],
              'id',
            ).reverse(), // 리버스 두번 쓴 이유는 순서 보장 및 concatList에 있는 데이터가 더 우선순위가 높기 떄문에 지워지지 않게 하기 위함.
          );

          // 생성 완료 되면 데이터 업데이트.
          if (
            aiProfileInfo?.requestId &&
            aiProfileInfo?.portraitFile === null
          ) {
            const findImage = datalist.find(
              (item) => item.id === aiProfileInfo?.requestId,
            );
            if (findImage) {
              onClickMyImage(findImage);
            }
          }
        }
      }
    },
  });

  const [getSavedAiCharacterImage] = useLazyQuery<
    {
      getSelectedRequestAICharacter: {
        chrId?: number;
        imageFile: ImageFile;
        uploadImage: ImageFile;
        id: number;
      };
    },
    { storyId: number; chrId: number }
  >(GET_SELECTED_REQUEST_AI_CHARACTER, {
    fetchPolicy: 'no-cache', // 캐시를 사용하지 않고 항상 새로운 데이터를 가져옵니다
  });

  const [imageConfirmFailRequest] = useMutation<void, { id: number }>(
    CONFIRM_FAILED_REQUEST_AI_CHARACTER,
  );

  const onGetMainCharacterList = async () => {
    if (playChapterInfo?.playChapter.storyId) {
      const res = await getMainCharacterList({
        variables: { storyId: playChapterInfo?.playChapter.storyId },
      });
      setCharacterList(res.data?.listMainCharacters.list);
      if (!selectedCharacter && res.data?.listMainCharacters.list?.[0]) {
        const defaultProfileData = res.data?.listMainCharacters.list.map(
          (item) => {
            return {
              characterId: item.id,
              defaultProfileImageFile: item.imageFile,
              firstName: item.id === -1 && !item.isCustom ? '' : item.firstName,
              lastName: item.id === -1 && !item.isCustom ? '' : item.lastName,
              portraitFile: item.portraitFile,
              displayName: item.displayName,
              usedAd: item.viewAdCount ?? 0,
              totalAd: onboarding?.maxAdCount ?? 0,
              requestId: item.requestId ? item.requestId : -1,
              latestCreatedAt: item.latestCreatedAt,
            } as AiProfileInfo;
          },
        );
        setAiProfileInfoArray(defaultProfileData);
      }
      return res.data?.listMainCharacters.list;
    }
  };

  const onGetAiCharacterSessionId = async () => {
    // const res = await getAiCharacterSessionId();
    // setAiCharacterSessionId(res.data?.requestSession.sessionId);
    // return res.data?.requestSession.sessionId;
  };

  const onGetAiCharacterPromptOptions = useCallback(async () => {
    if (playChapterInfo?.playChapter.storyId && selectedCharacter) {
      const res = await getAiCharacterPromptOptions({
        variables: {
          storyId: playChapterInfo?.playChapter.storyId,
          gender: selectedCharacter?.gender,
        },
      });
      setAiCharacterPromptOptions(
        res.data?.getStorygameAdditionalPrompts.prompts,
      );
    }
  }, [
    getAiCharacterPromptOptions,
    playChapterInfo?.playChapter.storyId,
    selectedCharacter,
  ]);

  const onGetAiCharacterImageList = async (chrId?: number) => {
    if (aiCharacterSessionId) {
      await getAiCharacterImageList({
        variables: {
          chrId: chrId ?? -1,
          sessionId: aiCharacterSessionId,
        },
      });
    }
  };

  // 캐릭터 목록 클릭시 setAiProfileInfo에 세팅
  const onClickStoryGameCharacter = (data: AiProfileInfo) => {
    const findChar = characterList?.find((d) => d.id === data.characterId);

    if (
      aiProfileInfo &&
      (!aiProfileInfo?.firstName || !aiProfileInfo?.lastName)
    ) {
      // 이전에 선택된 캐릭터는 이름이 없으면 에러처리
      onChangeAiProfile(
        {
          isError: true,
        },
        aiProfileInfo.characterId,
      );
    }
    setSelectedCharacter(findChar);

    onChangeAiProfile(
      {
        defaultProfileImageFile: data.defaultProfileImageFile,
        profileFile: data.profileFile,
      },
      data.characterId,
    );
    onGetAiCharacterImageList(data.characterId);
  };

  const onClickMyImage = async (character: AiCharacterImageType) => {
    if (character.isDefault) {
      // 디폴트 이미지를 클릭했을 때
      onChangeAiProfile({
        portraitFile: character.imageFile,
        defaultProfileImageFile: character.profileImage,
        profileFile: null,
        requestId: character.id,
      });
    } else if (
      selectedCharacter?.imageFile.fileId === character.profileImage?.fileId
    ) {
      // 캐릭터 목록에 선택되어있는 내 이미지를 클릭했을 때
      onChangeAiProfile({
        portraitFile: character.imageFile,
        defaultProfileImageFile: character.profileImage,
        profileFile: null,
        requestId: character.id,
      });
    } else {
      if (character.imageFile?.link) {
        // 디폴트 이미지가아닌 생성 이미지인 경우 자동 크롭.
        const res = await cropImageTopCenterFromUrl(character.imageFile.link);

        onChangeAiProfile({
          defaultProfileImageFile: null,
          portraitFile: character.imageFile,
          profileFile: res,
          requestId: character.id,
        });
      } else {
        onChangeAiProfile({
          defaultProfileImageFile: null,
          portraitFile: character.imageFile,
          profileFile: null,
          requestId: character.id,
        });
      }
    }
  };

  const [createAiCharacterMutate] = useMutation<
    { requestAICharacter: { sessionId: string; request: RequestAICharacter } },
    RequestCreateAICharacter
  >(CREATE_AI_CHARACTER, {});

  const onCreateAiCharacter = async (data: RequestCreateAICharacter) => {
    if (!aiCharacterSessionId) {
      console.error('Session ID is not defined');
      return 4;
    }

    const storyId = playChapterInfo?.playChapter.storyId;
    if (!storyId) {
      console.error('Story ID is not defined');
      return 1;
    }

    if (!data.image) {
      console.error('Image file is not defined');
      return 2;
    }

    if (!selectedCharacter) {
      console.error('Character is not selected');
      return 3;
    }
    try {
      const res = await createAiCharacterMutate({
        variables: {
          ...data,
          storyId: storyId,
          sessionId: aiCharacterSessionId,
          chrId: selectedCharacter.id,
        },
      });
      setAiCharacterImageList(
        uniqBy(
          [
            {
              id: res.data?.requestAICharacter.request.id,
              imageFile: res.data?.requestAICharacter.request.imageFile,
              status: res.data?.requestAICharacter.request.status,
              isDefault: false,
            } as AiCharacterImageType,
          ].concat(aiCharacterImageList ?? []),
          'id',
        ),
      );
      onClickMyImage({
        id: res.data?.requestAICharacter.request.id,
        imageFile: res.data?.requestAICharacter.request.imageFile,
        status: res.data?.requestAICharacter.request.status,
        isDefault: false,
      } as AiCharacterImageType);

      onChangeAiProfile({ latestCreatedAt: new Date().toISOString() }); // 마지막 생성 시간 업데이트

      const playChapterStory = playChapterInfo?.playChapter.story;
      const playType =
        playChapterStory.playType === 'Storygame'
          ? 'storygame'
          : playChapterStory.playType === 'Interactive'
          ? 'interactive'
          : 'novel';
      const payType =
        (data?.useCoin || 0) > 0 ? 'coin' : data.viewAd ? 'ad' : 'free';

      if (isMobileDevice()) {
        onLogEventForApp('create_custom_character_complete', {
          story_id: String(storyId),
          story_bundle_id: playChapterStory?.bundle
            ? String(playChapterStory.bundle?.storyBundleId)
            : '',
          story_name: playChapterStory.name,
          story_language: playChapterStory.language?.code ?? '',
          story_genre: playChapterStory.genreKey?.type ?? '',
          play_type: playType,
          pay_type: payType,
        });
      } else {
        createCustomCharacterComplete({
          storyId: String(storyId),
          storyBundleId: playChapterStory?.bundle
            ? String(playChapterStory.bundle?.storyBundleId)
            : '',
          storyName: playChapterStory.name,
          storyLanguage: playChapterStory.language?.code ?? '',
          storyGenre: playChapterStory.genreKey?.type ?? '',
          playType: playType,
          payType: payType,
        });
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          SERVER_ERROR_CODE.AI_CHR_TOSS_PAYMENTS_REWARD_ALREADY_CLAIMED,
        )
      ) {
        throw new Error((error as ApolloError).message);
      }

      console.error('Error creating AI character:', error);
      throw new Error('Error creating AI character');
    }
  };

  const [updateCharacterMutate] = useMutation<
    void,
    {
      chrId: number;
      storyId: number;
      portraitImageId?: number;
      image?: File | null;
      imageId?: number; // image나 id 둘중 하나만 필요.
      lastName?: string;
      firstName?: string;
      requestId?: number;
    }
  >(UPSERT_STORYGAME_CHARACTER, {
    fetchPolicy: 'no-cache', // 캐시를 사용하지 않고 항상 새로운 데이터를 가져옵니다
  });

  const onCreateCharacter = async () => {
    // aiProfileInfoArray 배열중 firstname lastname이 없으면 오류 표시
    const isAllNameExist = aiProfileInfoArray.every(
      (item) => item.firstName && item.lastName,
    );
    // aiProfileInfoArray 배열중 image, imageId가 둘다 없으면 오류 표시
    const isAllImageExist = aiProfileInfoArray.every(
      (item) => item.profileFile || item.defaultProfileImageFile,
    );

    if (!isAllNameExist) {
      setStorygameAlertData({
        title: t('common_popup_description_error_occurred'),
        subtitle: t('storygame_ai_onboarding_popup_description_enter_all_name'),
        onConfirm: () =>
          setStorygameAlertData({
            ...storygameAlertData,
            isVisible: false,
          }),
        isVisible: true,
      });
      return;
    }

    if (!isAllImageExist) {
      setStorygameAlertData({
        title: t('common_popup_description_error_occurred'),
        subtitle: t(
          'storygame_ai_onboarding_popup_description_enter_all_image',
        ),
        onConfirm: () =>
          setStorygameAlertData({
            ...storygameAlertData,
            isVisible: false,
          }),
        isVisible: true,
      });
      return;
    }

    try {
      await Promise.all(
        aiProfileInfoArray.map(async (info) => {
          if (playChapterInfo) {
            if (info.characterId === undefined) return;
            const data = {
              // requestId: info?.requestId,
              chrId: info.characterId,
              storyId: playChapterInfo.playChapter.storyId,
              portraitImageId: info?.portraitFile?.fileId,
              image: info?.profileFile,
              imageId: info?.defaultProfileImageFile?.fileId,
              lastName: info?.lastName,
              firstName: info?.firstName,
            };
            return await updateCharacterMutate({ variables: data });
          }
        }),
      );
      await onGetAiCharacterImageList(selectedCharacter?.id);
      const res2 = await getMainCharacterList({
        variables: { storyId: playChapterInfo?.playChapter.storyId },
      });
      setCharacterList(res2.data?.listMainCharacters.list);

      const defaultProfileData = res2.data?.listMainCharacters.list.map(
        (item) => {
          if (item.id === -1) {
            const myInfo: (MyCharacterInfo & { popupId: number }) | undefined =
              {
                firstName: item.firstName,
                lastName: item.lastName,
                profileImageId: item.imageFile.fileId,
                popupId: onboarding?.popupId ?? -1,
              };

            myCharacterInfoRef.current = myInfo ?? myCharacterInfoRef.current; // 바뀐 정보 저장 시작할 떄 이 객체를 보고 이름 넣어줌
          } else {
            const otherInfo:
              | (OtherCharacterInfo & { popupId: number })
              | undefined = {
              originName: item.name,
              customFirstName: item.firstName,
              customLastName: item.lastName,
              profileImageFileId: item.imageFile.fileId,
              popupId: onboarding?.popupId ?? -1,
            };
            otherCharacterInfosRef.current?.push(otherInfo); // 바뀐 정보 저장 시작할 떄 이 객체를 보고 이름 넣어줌
          }

          return {
            characterId: item.id,
            defaultProfileImageFile: item.imageFile,
            firstName: item.firstName,
            lastName: item.lastName,
            portraitFile: item.portraitFile,
            displayName: item.displayName,
            usedAd: 0,
            totalAd: onboarding?.maxAdCount ?? 0,
            requestId: item.requestId ? item.requestId : -1,
          } as AiProfileInfo;
        },
      );

      if (defaultProfileData) setAiProfileInfoArray(defaultProfileData);

      setIsStoryGameEdit(false);
      setStorygameToastData({
        message: t('storygame_ai_onboarding_popup_description_save_character'),
        isVisible: true,
      });
    } catch (e) {
      setStorygameAlertData({
        title: t('common_popup_description_error_occurred'),
        subtitle: t(
          'storygame_ai_onboarding_popup_description_save_failed_character',
        ),
        onConfirm: () =>
          setStorygameAlertData({
            ...storygameAlertData,
            isVisible: false,
          }),
        isVisible: true,
      });
    }
  };

  const addOpenedStorygameIds = () => {
    if (playChapterInfo?.playChapter.storyId) {
      const newStoryId = playChapterInfo.playChapter.storyId;

      // 기존 ID 목록을 파싱하고 Set으로 변환
      const idsSet = new Set(
        JSON.parse(openedStorygameIds ?? '[]') as number[],
      );

      // 새로운 ID 추가 (중복이면 자동으로 무시됨)
      idsSet.add(newStoryId);

      // Set을 배열로 변환하고 JSON 문자열로 저장
      setOpenedStorygameIds(JSON.stringify(Array.from(idsSet)));
    }
  };

  // aiCharacterList로 구현된 리스트를  selectedCharacter 다음 index를 찾아 onNext, onPrev 함수 구현

  // 이전 캐릭터 선택 함수
  const selectPreviousCharacter = () => {
    if (!characterList || characterList.length === 0) return;

    const currentIndex = selectedCharacter
      ? characterList.findIndex((char) => char.id === selectedCharacter.id)
      : -1;

    let newIndex;
    if (currentIndex === -1 || currentIndex === 0) {
      newIndex = characterList.length - 1;
    } else {
      newIndex = currentIndex - 1;
    }

    onClickStoryGameCharacter(aiProfileInfoArray[newIndex]);
  };

  // 다음 캐릭터 선택 함수
  const selectNextCharacter = () => {
    if (!characterList || characterList.length === 0) return;

    const currentIndex = selectedCharacter
      ? characterList.findIndex((char) => char.id === selectedCharacter.id)
      : -1;

    let newIndex;
    if (currentIndex === -1 || currentIndex === characterList.length - 1) {
      newIndex = 0;
    } else {
      newIndex = currentIndex + 1;
    }

    onClickStoryGameCharacter(aiProfileInfoArray[newIndex]);
  };

  // 저장하기 전에 이름 다 들어있는지 체크하는 함수
  const isNameMissingForSave = () => {
    const isAllNameExist = aiProfileInfoArray.every(
      (item) => item.firstName && item.lastName,
    );
    return isAllNameExist;
  };

  const onClickHomeIcon = () => {
    setStorygameAlertData({
      title: t('storygame_ai_onboarding_popup_title_quit_guide'),
      subtitle: t('storygame_ai_onboarding_popup_description_quit_guide'),
      confirmText: t('storygame_popup_button_quit'),
      onConfirm: () => {
        setStorygameAlertData({
          ...storygameAlertData,
          isVisible: false,
        });

        isMobileDevice()
          ? goBackForApp()
          : router.replace(`/story/${playChapterInfo?.playChapter.storyId}`);
      },
      onCancel: () => {
        setStorygameAlertData({
          ...storygameAlertData,
          isVisible: false,
        });
      },
      isVisible: true,
    });
    //
  };

  // TODO: ai profile 의 성, 이름, 이미지 변경, 삭제, 추가 등의 기능이 필요할 경우 추가
  const onChangeAiProfile = (
    newAiProfileInfo: AiProfileInfo,
    charId?: number,
  ) => {
    // newAiProfileInfo에 있는 데이터만 aiProfileInfo에 업데이트
    // const filteredNewAiProfileInfo = Object.fromEntries(
    //   Object.entries(newAiProfileInfo).filter(([key, value]) => value !== undefined)
    // );

    if (!selectedCharacter && charId === undefined) {
      return;
    }

    setAiProfileInfoArray((prevArray) => {
      const id = charId !== undefined ? charId : selectedCharacter!.id;

      const aiProfileInfoIndex =
        prevArray?.findIndex((item) => item.characterId === id) ?? -1;

      if (aiProfileInfoIndex === -1) return prevArray;

      const updatedArray = [...prevArray];
      updatedArray[aiProfileInfoIndex] = Object.keys(
        newAiProfileInfo,
      ).reduce<AiProfileInfo>(
        (acc, key) => {
          const typedKey = key as keyof AiProfileInfo;
          const value = newAiProfileInfo[typedKey];

          if (value !== undefined) {
            // Type assertion을 사용하여 타입 에러 해결
            (acc[typedKey] as any) = value;
          }

          return acc;
        },
        { ...updatedArray[aiProfileInfoIndex] },
      );

      return updatedArray;
    });
  };

  const removeSessionId = () => {
    const sessionArray = JSON.parse(
      localStorage.getItem(STORAGE_KEY.STORY_GAME_SESSION_ID) ?? '[]',
    ) as { storyId: number; sessionId: string }[];

    const findIndex = sessionArray.findIndex(
      (item) => item.storyId === playChapterInfo?.playChapter.storyId,
    );
    //findIndex 하나만 삭제
    if (findIndex !== -1) {
      sessionArray.splice(findIndex, 1);
    }

    localStorage.setItem(
      STORAGE_KEY.STORY_GAME_SESSION_ID,
      JSON.stringify(sessionArray),
    );
  };

  useEffect(() => {
    // 스겜 세션id발급. 이게 바뀌면 기존 생성된 이미지 목록이 사라짐. (저장된건 유지)
    if (sessionData?.requestSession.sessionId) {
      setAiCharacterSessionId(sessionData.requestSession.sessionId);
      if (playChapterInfo?.playChapter.storyId) {
        const sessionArray = JSON.parse(
          localStorage.getItem(STORAGE_KEY.STORY_GAME_SESSION_ID) ?? '[]',
        ) as { storyId: number; sessionId: string }[];
        sessionArray.push({
          storyId: playChapterInfo?.playChapter.storyId,
          sessionId: sessionData.requestSession.sessionId,
        });
        localStorage.setItem(
          STORAGE_KEY.STORY_GAME_SESSION_ID,
          JSON.stringify(sessionArray),
        );
      }
    }
  }, [sessionData, setAiCharacterSessionId]);

  useEffect(() => {
    if (
      aiProfileInfoArray &&
      aiProfileInfoArray.length > 0 &&
      selectedCharacter
    ) {
      // aiProfileInfoArray이 변경되면 캐릭터 선택된 캐릭터의 aiProfileInfo를 찾아서 세팅. onChangeAiProfile에서 infoArray를 변경하기때문에 선택된애 찾아서 변경!
      // 기존에 aiProfileInfo 하나로 관리 하게 짜두었다가 전체 한번에 저장으로 바뀌면서 호환성 유지를 위해 이렇게 함.
      const findIndex = aiProfileInfoArray.findIndex((item) => {
        return selectedCharacter.id === item.characterId;
      });
      if (findIndex !== -1) {
        setAiProfileInfo(aiProfileInfoArray[findIndex]);
      }
    }
  }, [aiProfileInfoArray]);

  useEffect(() => {
    if (
      aiProfileInfoArray &&
      aiProfileInfoArray.length > 0 &&
      (characterList?.length ?? 0) > 0
    ) {
      if (!selectedCharacter) {
        onClickStoryGameCharacter(aiProfileInfoArray[0]);
      }
    }
  }, [characterList]);

  useEffect(() => {
    if (openedStorygameIds && playChapterInfo?.playChapter.storyId) {
      const ids = JSON.parse(openedStorygameIds) as number[];

      ids.includes(playChapterInfo?.playChapter.storyId)
        ? setIsShowPromotionStorygame(false)
        : setIsShowPromotionStorygame(true);
    } else {
      setIsShowPromotionStorygame(true);
    }
  }, [openedStorygameIds, playChapterInfo?.playChapter.storyId]);

  useEffect(() => {
    if (onboarding?.type === 'StorygameProfile') {
      const init = async () => {
        const res = await onGetMainCharacterList();
        if (res?.every((item) => item.isCustom === true)) {
          setIsStoryGameEdit(false);
        }
      };
      init();
    }
  }, [onboarding]);

  //--- 여기까지 스겜전용 변수들

  useEffect(() => {
    setPlayStory(playChapterInfo?.playChapter?.story);
    setOnboardings(playChapterInfo?.playChapter?.onboardingPopups || []);
    setOnboarding(
      playChapterInfo?.playChapter?.onboardingPopups?.[onboardingIndex.current],
    );
    playChapterInfo?.playChapter?.story.mainImageFile;
  }, [playChapterInfo]);

  const [presignedUrlsMutate, { loading: presignedUrlsLoading }] =
    useLazyQuery<{
      getPresignedUrls: GetPresignedUrlsOutput[];
    }>(GET_PRESIGNED_URLS, {
      onCompleted: (res) => {
        if (res.getPresignedUrls) {
          presignedUrlsRef.current = res.getPresignedUrls?.map((item) => {
            const profileImage = choiceProfilesRef.current.find(
              (image) =>
                `${image.popupId}_${image.file?.name}` === item.fileName,
            );
            return {
              ...item,
              file: profileImage?.file,
              popupId: profileImage?.popupId,
            };
          });
        }
        updateImages();
      },
      // onError: (error) => {},
    });

  useEffect(() => {
    setIsLoading(presignedUrlsLoading);
  }, [presignedUrlsLoading]);

  const getNextOnboarding = () => {
    const currentOnBoarding = onboardings[onboardingIndex.current];
    if ((currentOnBoarding?.visibleConditions?.length || 0) < 1) {
      return currentOnBoarding;
    }

    const condition = currentOnBoarding?.visibleConditions?.find(
      (condition) => {
        if (condition.property === '__playerClass__') {
          const isVisible = choiceProfilesRef.current?.find((choiceProfile) =>
            condition?.inequality === 'Equal'
              ? condition.value === choiceProfile?.playerClassId
              : condition.value !== choiceProfile?.playerClassId,
          );
          return isVisible;
        } else {
          if (choicePropsRef?.current) {
            const propsList = Object.entries(choicePropsRef?.current);
            const isVisible = propsList.find(
              (props) =>
                props[0] === condition.property &&
                (condition?.inequality === 'Equal'
                  ? condition.value === props[1]
                  : condition?.inequality === 'NotEqual'
                  ? condition.value !== props[1]
                  : condition?.inequality === 'EqualOrLessThan'
                  ? condition.value >= +props[1]
                  : condition?.inequality === 'EqualOrMoreThan'
                  ? condition.value <= +props[1]
                  : condition?.inequality === 'MoreThan'
                  ? condition.value < +props[1]
                  : condition?.inequality === 'LessThan' &&
                    condition.value > +props[1]),
            );
            return isVisible;
          } else {
            return undefined;
          }
        }
      },
    );

    return condition && currentOnBoarding;
  };

  const handlePresignedUrls = () => {
    const presignedUrls = choiceProfilesRef.current
      ?.filter((item) => item.file)
      ?.map((item) => {
        return `${item.popupId}_${item.file?.name}`;
      });
    if (presignedUrls ? presignedUrls.length : -1 > 0) {
      const data = {
        storageType: 'StoryFiles',
        isTemp: true,
        fileNames: presignedUrls,
      };

      presignedUrlsMutate({
        variables: data,
      });
    } else {
      onFinishOnboarding();
    }
  };

  const updateImages = async () => {
    const apiCalls = presignedUrlsRef.current.map(
      (item) => item.file && onFileUpload(item.url, item.file),
    );
    setIsLoading(true);
    const results = await Promise.all(apiCalls)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        return undefined;
      });

    setIsLoading(false);

    if (results) {
      const profileInfos = presignedUrlsRef.current.map((item) => {
        return {
          hashId: item.hashId,
          fileName: item.fileName,
          isTemp: item.isTemp,
          storageType: item.storageType,
          popupId: item.popupId,
        };
      });

      const myCustomProfile = profileInfos.find(
        (item) => item.popupId === myCharacterInfoRef.current?.popupId,
      );

      if (myCustomProfile && myCharacterInfoRef.current) {
        myCharacterInfoRef.current = {
          ...myCharacterInfoRef.current,
          customProfileFileInfo: {
            hashId: myCustomProfile.hashId,
            fileName: myCustomProfile.fileName,
            isTemp: myCustomProfile.isTemp,
            storageType: myCustomProfile.storageType,
          },
        };
      }

      const otherCustomProfiles = profileInfos.filter((item) =>
        otherCharacterInfosRef.current.find(
          (info) => info.popupId === item.popupId,
        ),
      );

      if (otherCustomProfiles && otherCharacterInfosRef.current) {
        otherCharacterInfosRef.current = otherCharacterInfosRef.current.map(
          (item) => {
            const otherCustomProfile = otherCustomProfiles.find(
              (info) => info.popupId === item.popupId,
            );
            return otherCustomProfile
              ? {
                  ...item,
                  customProfileFileInfo: {
                    hashId: otherCustomProfile.hashId,
                    fileName: otherCustomProfile.fileName,
                    isTemp: otherCustomProfile.isTemp,
                    storageType: otherCustomProfile.storageType,
                  },
                }
              : item;
          },
        );
      }

      onFinishOnboarding();
    }
  };

  const onFileUpload = async (url: string, file: File) => {
    try {
      const result = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'image/jpeg',
        },
        body: file,
      });
      return result;
    } catch (e) {
      return e;
    }
  };

  const onNextPopup = () => {
    currentNameRef.current = undefined;
    onboardingIndex.current++;
    if (onboardingIndex.current < onboardings.length) {
      const nextOnboarding = getNextOnboarding();
      nextOnboarding ? setOnboarding(nextOnboarding) : onNextPopup();
    } else {
      handlePresignedUrls();
    }
  };

  const onFinishOnboarding = () => {
    const choiceProfile = choiceProfilesRef.current?.find(
      (item) => item.playerClassId,
    );
    if (myCharacterInfoRef.current) {
      delete myCharacterInfoRef.current['popupId'];
    }
    if (otherCharacterInfosRef.current) {
      otherCharacterInfosRef.current?.map((item) => {
        delete item['popupId'];
        return item;
      });
    }

    onFinish(
      myCharacterInfoRef.current,
      choicePropsRef.current,
      otherCharacterInfosRef.current,
      choiceProfile?.playerClassId,
    );
    onboardingIndex.current = 0;
  };

  const setName = (
    otherCharacterInfo?: OtherCharacterInfo & { popupId: number },
    myCharacterInfo?: MyCharacterInfo & { popupId: number },
  ) => {
    otherCharacterInfo &&
      otherCharacterInfosRef.current?.push(otherCharacterInfo);
    myCharacterInfoRef.current = myCharacterInfo || myCharacterInfoRef.current;
    onNextPopup();
  };

  const onNextChoice = (
    mProps?: Record<string, number | string>,
    profileInfo?: CustomProfile,
  ) => {
    choicePropsRef.current = margeChoiceProps(
      choicePropsRef.current || {},
      mProps || {},
    );
    if (profileInfo) {
      choiceProfilesRef.current.push(profileInfo);
    }
    onNextPopup();
  };

  const onShowProfile = (type: 'default' | 'user') => {
    if (type === 'default') {
      choiceProfilesRef.current = choiceProfilesRef.current.filter(
        (item) => item.popupId !== onboarding?.popupId,
      );
      setProfileType(undefined);
    } else {
      if ((onboarding?.popupProfileImages.length || -1) > 0) {
        isChooseProfileRef.current = true;
        setProfileType('ChooseProfile');
      } else {
        setProfileType('ImageUpload');
      }
    }
  };

  const onUpdateProfileImage = (currentName?: {
    firstName: string;
    lastName?: string;
  }) => {
    currentNameRef.current = currentName;
    if (
      onboarding?.canUploadProfileImage &&
      onboarding.popupProfileImages?.length < 1
    ) {
      setProfileType('UpdateProfile');
    } else if ((onboarding?.popupProfileImages.length || -1) > 0) {
      isChooseProfileRef.current = true;
      setProfileType('ChooseProfile');
    } else {
      setProfileType('ImageUpload');
    }
  };

  const onSelectImage = () => {
    setProfileType('ImageUpload');
  };

  const onCloseSelectImage = (customProfile?: {
    file: File;
    fileUrl: string;
  }) => {
    if (customProfile && onboarding) {
      choiceProfilesRef.current = [
        ...choiceProfilesRef.current.filter(
          (item) => item.popupId !== onboarding.popupId,
        ),
        {
          popupId: onboarding.popupId,
          file: customProfile.file,
          fileUrl: customProfile.fileUrl,
        },
      ];
    }
    setProfileType(isChooseProfileRef.current ? 'ChooseProfile' : undefined);
  };

  const onConfirmChooseProfile = (image: CustomProfile) => {
    setProfileType(undefined);
    choiceProfilesRef.current = [
      ...choiceProfilesRef.current.filter(
        (item) => item.popupId !== image.popupId,
      ),
      image,
    ];
    isChooseProfileRef.current = false;
  };

  const onCloseUpdateProfile = () => {
    setProfileType(undefined);
  };

  const getStoryGamePopupBg = () => {
    /**
     * introImageFile = 1:1  bannerImageFile = 16:9 wideImageFile = 4:3
     * isPC가 false인 경우 우선 순위에 따라서 1:1, 4:3, 16:9 이미지를 가져온다.
     * isPC가 true 경우 우선 순위에 따라서 16:9, 4:3, 1:1 이미지를 가져온다.
     * */
    if (isPC) {
      // PC 우선순위: 16:9 > 4:3 > 1:1
      return (
        (
          playChapterInfo?.playChapter.story.bannerImageFile ||
          playChapterInfo?.playChapter.story.wideImageFile ||
          playChapterInfo?.playChapter.story.introImageFile
        )?.link ?? ''
      );
    } else {
      // 모바일 우선순위: 1:1 > 4:3 > 16:9
      return (
        (
          playChapterInfo?.playChapter.story.introImageFile ||
          playChapterInfo?.playChapter.story.wideImageFile ||
          playChapterInfo?.playChapter.story.bannerImageFile
        )?.link ?? ''
      );
    }
  };

  return (
    <OnboardingPopupContext.Provider
      value={{
        playStory,
        onboardings,
        profileType,
        onboarding,
        isLoading,
        onboardingIndex,
        choiceProfilesRef,
        currentNameRef,
        aiProfileType,
        onShowProfile,
        onCloseSelectImage,
        onSelectImage,
        onConfirmChooseProfile,
        onNextPopup,
        onUpdateProfileImage,
        setName,
        onNextChoice,
        onCloseUpdateProfile,
        setAiProfileType,
        onChangeAiProfile,
        aiProfileInfoArray,
        onCreateAiCharacter,
        onGetAiCharacterSessionId,
        aiCharacterSessionId,
        onGetAiCharacterPromptOptions,
        aiCharacterPromptOptions,
        onGetAiCharacterImageList,
        aiCharacterImageList,
        onGetMainCharacterList,
        characterList,
        onClickStoryGameCharacter,
        selectedCharacter,
        onCreateCharacter,
        aiProfileInfo,
        openedStorygameIds,
        addOpenedStorygameIds,
        isShowPromotionStorygame,
        setIsShowPromotionStorygame,
        storygamePopupBg: getStoryGamePopupBg(),
        isStoryGameEdit,
        setIsStoryGameEdit,
        selectPreviousCharacter,
        selectNextCharacter,
        storygameAlertData,
        setStorygameAlertData,
        isNameMissingForSave,
        onClickHomeIcon,
        onClickMyImage,
        storygameToastData,
        setStorygameToastData,
        removeSessionId,
      }}
    >
      {children}
    </OnboardingPopupContext.Provider>
  );
};

export const useOnboardingPopupContext = () => {
  const state = useContext(OnboardingPopupContext);
  if (!state) throw new Error('OnboardingPopupContext is undefined!');
  return state;
};

export default OnboardingPopupProvider;
